import classNames from 'classnames'
import Link from 'next/link'
import { Fragment, useEffect, useState } from 'react'
import { useRef } from 'react'
import BSCard from 'react-bootstrap/Card'
import Card from 'react-bootstrap/Card'
import ListGroup from 'react-bootstrap/ListGroup'
import Navbar from 'react-bootstrap/Navbar'

import Icon from '@/components/Icon/Icon'
import Scrollspy from '@/components/Scrollspy/Scrollspy'
import { useAds } from '@/contexts/ads'
import { AuthorType } from '@/types/wordpress'
import { formatUrl } from '@/utils/helpers'

import { TocType } from './GuidePage'
import styles from './GuideSidebar.module.scss'

type Props = {
  author: AuthorType
  title: string
  toc: TocType
}

const GuideSidebar = ({ author, title, toc }: Props) => {
  const { loadAds } = useAds()
  const menuRef = useRef<HTMLDivElement>(null)
  const [shouldScroll, setShouldScroll] = useState(false)

  const headers = toc.flatMap(({ rows }) => rows.map(({ heading }) => heading))
  const hrefs = toc.flatMap(({ header, rows }) => rows.map((row) => '#' + formatUrl(`${header}-${row.heading}`)))

  useEffect(() => {
    const windowDelta = 60 + (loadAds ? 90 : 0)
    console.log('loadAds', loadAds, 'windowDelta', windowDelta)

    const handleResize = () => {
      if (menuRef.current) {
        const newShouldScroll = menuRef.current.offsetHeight > window.innerHeight - windowDelta
        console.log(`${menuRef.current.offsetHeight} > ${window.innerHeight - windowDelta} = ${newShouldScroll}`)
        setShouldScroll(newShouldScroll)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [loadAds])

  return (
    <>
      <Scrollspy offset={-100} sectionRefs={toc.flatMap(({ rows }) => rows.map(({ ref }) => ref))}>
        {({ currentElementIndexInViewport }) => {
          const menu = toc.map(({ header, icon, rows }, i) => {
            const numPrevSections = toc.slice(0, i).reduce((acc, { rows }) => acc + rows.length, 0)
            const chapterActive =
              currentElementIndexInViewport >= numPrevSections &&
              currentElementIndexInViewport < numPrevSections + rows.length
            return (
              <Fragment key={header}>
                <div className='mb-1'>
                  <Link
                    className={classNames(styles.header, { [styles.active]: chapterActive })}
                    href={'#' + formatUrl(header)}
                  >
                    <div className='align-items-center d-flex gap-2'>
                      <i className={icon} />
                      <span>{header}</span>
                    </div>
                  </Link>
                  <BSCard className='w-100 shadow-sm'>
                    <ListGroup activeKey={hrefs[currentElementIndexInViewport]} numbered variant='flush'>
                      {rows.map((row, j) => {
                        const href = hrefs[numPrevSections + j]
                        return (
                          <ListGroup.Item action href={href} key={row.key}>
                            {row.heading}
                          </ListGroup.Item>
                        )
                      })}
                    </ListGroup>
                  </BSCard>
                </div>
              </Fragment>
            )
          })

          return (
            <>
              {/* Mobile */}
              <Navbar
                as={Card}
                className={classNames(styles.mobileContainer)}
                collapseOnSelect
                expand={false}
                sticky='top'
              >
                <Navbar.Brand className={styles.mobileHeading}>
                  <div className='align-items-center d-flex gap-2'>
                    <Icon icon='book' />
                    <span>{headers[currentElementIndexInViewport] || title}</span>
                  </div>
                </Navbar.Brand>
                <Navbar.Toggle />
                <Navbar.Collapse className={styles.mobileCollapse}>{menu}</Navbar.Collapse>
              </Navbar>

              {/* Desktop */}
              <div className={classNames(styles.container, { [styles.withScrollbar]: shouldScroll })}>
                <Link href={`/articles/author/${author.slug}`} target='_blank'>
                  <BSCard className='d-flex flex-row gap-3 p-3 mb-2 shadow-sm'>
                    <img alt='' className={styles.avatar} src={author.avatarUrl} />
                    <div className='w-100'>
                      <div className={styles.authorLabel}>Author</div>
                      <div className={styles.author}>{author.name}</div>
                    </div>
                  </BSCard>
                </Link>
                <div className={styles.sticky}>
                  <div className={classNames({ [styles.scroll]: shouldScroll, [styles.noAds]: loadAds === false })}>
                    <div className='d-flex flex-column gap-2' ref={menuRef}>
                      {menu}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }}
      </Scrollspy>
    </>
  )
}

export default GuideSidebar
